import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/react';
import { PageTransition, StaggerItem } from '../app/page-transition.js';
import { ApiKeysSection } from './api-keys-section.js';
import { ProfileSection } from './profile-section.js';
import { LanguageSection } from './language-section.js';
import { CurrencySection } from './currency-section.js';
import { BillingAddressSection } from './billing-address-section.js';
import { GdprSection } from './gdpr-section.js';
import { ContextualOnboardingOverlay } from '../../components/ui/contextual-spotlight/contextual-onboarding-overlay.js';

interface SectionMeta {
  id: string;
  labelKey: string;
  render: () => ReactNode;
}

const SECTIONS: readonly SectionMeta[] = [
  { id: 'profile', labelKey: 'profile.heading', render: () => <ProfileSection /> },
  { id: 'language', labelKey: 'language.heading', render: () => <LanguageSection /> },
  { id: 'currency', labelKey: 'currency.heading', render: () => <CurrencySection /> },
  { id: 'billing', labelKey: 'billing.heading', render: () => <BillingAddressSection /> },
  { id: 'api-keys', labelKey: 'apiKeys.heading', render: () => <ApiKeysSection /> },
  { id: 'gdpr', labelKey: 'gdpr.heading', render: () => <GdprSection /> },
];

function SettingsHero() {
  const { t } = useTranslation('settings');
  const { isLoaded, user } = useUser();

  const email = user?.primaryEmailAddress?.emailAddress;
  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.username || email || '';

  return (
    <div data-testid="settings-hero">
      <p className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
        Account
      </p>
      <h1 className="mt-4 max-w-[20ch] font-sans text-[clamp(36px,6vw,56px)] font-light leading-[1.05] tracking-[-0.03em] text-primary">
        {isLoaded && displayName ? (
          <>
            Hello, <em className="not-italic text-muted">{displayName}.</em>
          </>
        ) : (
          t('heading')
        )}
      </h1>
      <p className="mb-12 mt-4 max-w-[60ch] text-[16px] leading-[1.6] text-secondary">
        Your AgentBrush account. Update your profile, manage CLI API keys, billing address, and
        privacy preferences.{' '}
        {email ? <span className="font-mono text-[14px] text-muted">{email}</span> : null}
      </p>

      <div className="mb-2 flex flex-wrap items-center gap-3">
        <Link
          to="/billing"
          data-testid="settings-hero-manage-subscription"
          className="inline-flex items-center gap-2 rounded-md bg-accent-lime px-4 py-2.5 text-[13px] font-semibold text-canvas transition-[filter] hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {t('hero.manageSubscription')}
        </Link>
        <Link
          to="/getting-started"
          className="inline-flex items-center gap-2 px-1 py-2.5 text-[13px] text-secondary transition-colors hover:text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          Connect your agent →
        </Link>
      </div>
    </div>
  );
}

function SectionCard({
  id,
  label,
  first = false,
  children,
}: {
  id: string;
  label: string;
  first?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      id={`settings-${id}`}
      data-settings-section={id}
      className={`scroll-mt-24 ${first ? 'pb-14' : 'border-t border-white/[0.06] py-14'}`}
    >
      <p className="mb-6 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
        {label}
      </p>
      {children}
    </section>
  );
}

export function SettingsPage() {
  const { t } = useTranslation('settings');
  return (
    <PageTransition>
      <ContextualOnboardingOverlay sectionId="settings" />
      {/* Fluid full-width container. Content stays left-aligned at 960px so
       * the page heading shares the same left edge as the brand in the nav. */}
      <div className="w-full px-6 pb-32 pt-16 standard:px-10">
        <div className="w-full max-w-[960px]">
          <StaggerItem>
            <SettingsHero />
          </StaggerItem>

          <div className="mt-12 flex flex-col">
            {SECTIONS.map(({ id, labelKey, render }, idx) => (
              <StaggerItem key={id}>
                <SectionCard id={id} label={t(labelKey)} first={idx === 0}>
                  {render()}
                </SectionCard>
              </StaggerItem>
            ))}
          </div>
        </div>
      </div>
    </PageTransition>
  );
}
